import React, { useEffect } from 'react';

import { useCycleAd, useSetupAd } from '@client/hooks';
import useLayoutTypeSub from '@client/hooks/useLayoutTypeSub';
import {
  BannerAdObj,
  debugLog,
  LayoutType,
  NativeAdObj,
  PlacementId,
  UNLEASH_FEATURE_NAME,
  VideoAdObj
} from '@schibsted-nmp/advertising-shared';

import { usePlacementStyling } from '../../../hooks/usePlacementStyling';
import { Placeholder } from '../../placeholder/Placeholder';
import BannerAdComponent from '../banner/BannerAdComponent';
import NativeAdComponent from '../native/NativeAdComponent';
import VideoAdComponent from '../video/VideoAdComponent';
import { isFeatureEnabled } from '../../../state/reducer';
import { showTag } from '../../../xandr/xandr';

export type AdSwitchProps = {
  containerId: string;
  placementId: PlacementId;
  initialLayoutType?: LayoutType;
};

export function AdSwitchComponent(props: AdSwitchProps) {
  const { placementId, containerId, initialLayoutType } = props;
  const targetId = useCycleAd(placementId);
  const layoutType = useLayoutTypeSub(initialLayoutType || 'grid');

  const { adObj, status, isRequested } = useSetupAd({
    placementId,
    targetId,
    containerId
  });

  const className = usePlacementStyling(
    placementId,
    containerId,
    targetId,
    adObj,
    status
  );

  useEffect(() => {
    if (
      isRequested &&
      isFeatureEnabled(UNLEASH_FEATURE_NAME.forceShowTagEarly)
    ) {
      debugLog(`Forced show tag for ${placementId}`);
      showTag(placementId);
    }
  }, [isRequested]);

  if (!adObj) return null;
  switch (adObj?.adType) {
    case 'native':
      return (
        <NativeAdComponent
          adObj={adObj as NativeAdObj}
          targetId={targetId}
          placementId={placementId}
          status={status}
          layoutType={layoutType}
        />
      );
    case 'banner':
      return (
        <BannerAdComponent
          adObj={adObj as BannerAdObj}
          targetId={targetId}
          placementId={placementId}
          className={className}
        />
      );
    case 'video':
      return (
        <VideoAdComponent
          adObj={adObj as VideoAdObj}
          targetId={targetId}
          placementId={placementId}
        />
      );
    default:
      return <Placeholder id={targetId} />;
  }
}
